import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

const truncateText = (text: string, maxWidth: number, pdfInstance: jsPDF): string => {
  if (pdfInstance.getTextWidth(text) <= maxWidth) return text;
  let truncated = text;
  while (truncated.length > 0 && pdfInstance.getTextWidth(truncated + "...") > maxWidth) {
    truncated = truncated.slice(0, -1);
  }
  return truncated + "...";
};

export const exportItemsToPDF = async (onProgress?: (progress: number) => void) => {
  const elements = document.querySelectorAll(".qr-preview");
  if (!elements || elements.length === 0) {
    alert("No items to export!");
    return;
  }

  // A4 size: 210mm x 297mm
  const pdf = new jsPDF("p", "mm", "a4");

  try {
    const fontResponse = await fetch('/fonts/Roboto-Bold.ttf');
    if (fontResponse.ok) {
      const fontBuffer = await fontResponse.arrayBuffer();
      const fontBase64 = arrayBufferToBase64(fontBuffer);
      pdf.addFileToVFS("Roboto-Bold.ttf", fontBase64);
      pdf.addFont("Roboto-Bold.ttf", "Roboto", "bold");
      pdf.setFont("Roboto", "bold");
    } else {
      console.warn("Could not fetch Roboto font, falling back to default.");
    }
  } catch (err) {
    console.error("Failed to load Roboto font", err);
  }

  const margin = 7.5; // Reduced margin by half
  const cols = 4;
  const rows = 5;
  const maxItemsPerPage = cols * rows;
  
  const usableWidth = 210 - margin * 2;
  const itemWidth = usableWidth / cols;
  const itemHeight = (297 - margin * 2) / rows;
  // Decrease imageSize to make items smaller and increase spacing
  const imageSize = Math.min(itemWidth - 10, 32); 

  let currentItem = 0;

  for (let i = 0; i < elements.length; i++) {
    const el = elements[i];
    
    // Elements to capture
    const displayEl = el.querySelector(".qr-display") as HTMLElement;
    const noteEl = el.querySelector(".the-note input, .the-note span") as HTMLElement;
    
    if (!displayEl) continue;

    try {
      // Use html2canvas to capture display element
      const canvas = await html2canvas(displayEl, {
        scale: 2, // Higher resolution
        backgroundColor: "#ffffff",
        logging: false,
      });
      
      const imgData = canvas.toDataURL("image/png");
      const noteText = noteEl ? (noteEl as HTMLInputElement).value || noteEl.innerText || noteEl.textContent || "" : "";

      // Calculate position
      const pageIndex = Math.floor(currentItem / maxItemsPerPage);
      if (pageIndex > 0 && currentItem % maxItemsPerPage === 0) {
        pdf.addPage();
        pdf.setFont("Roboto", "bold"); // ensure font is used on new pages
      }

      const indexOnPage = currentItem % maxItemsPerPage;
      const col = indexOnPage % cols;
      const row = Math.floor(indexOnPage / cols);

      const x = margin + col * itemWidth;
      const y = margin + row * itemHeight;

      pdf.setFontSize(9);
      
      // Truncate text to 1 line with ellipsis if it exceeds imageSize
      const singleLineText = noteText ? noteText.replace(/\n/g, ' ').trim() : "";
      const textToDraw = singleLineText !== "" ? truncateText(singleLineText, imageSize, pdf) : "";
      
      // Always reserve space for 1 line of note (4mm text + 2mm gap = 6mm)
      // This ensures items in the same row align perfectly.
      const fixedNoteSpace = 6;
      const totalItemContentHeight = imageSize + fixedNoteSpace;

      // Content top Y coordinate
      const contentY = y + Math.max(0, (itemHeight - totalItemContentHeight) / 2);
      let currentY = contentY;

      // Draw Note (Top)
      if (textToDraw !== "") {
        const textX = x + itemWidth / 2;
        const textY = currentY + 3; // Baseline adjustment
        pdf.text(textToDraw, textX, textY, { align: "center" });
      }
      
      // Always shift Y by the fixed note space so images align
      currentY += fixedNoteSpace;

      // Draw Image (Below Note)
      const imgX = x + (itemWidth - imageSize) / 2;
      const imgY = currentY;
      pdf.addImage(imgData, "PNG", imgX, imgY, imageSize, imageSize);

      currentItem++;
      if (onProgress) {
        onProgress(Math.round(((i + 1) / elements.length) * 100));
      }
    } catch (err) {
      console.error("Error capturing element", err);
    }
  }

  const now = new Date();
  const timestamp = `${now.getFullYear()}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}_${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}`;
  pdf.save(`export_${timestamp}.pdf`);
};

